import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, patch, del } from '@/lib/apiClient';
import { API_URL } from '@/_config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WorkflowFilter {
  id: string;
  workflow_id: string;
  field: string;
  operator: string;
  value: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface WorkflowLead {
  id: string;
  workflow_id: string;
  lead_provider_id: string;
  is_checked: boolean;
  status: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkflowSegment {
  id: string;
  name: string;
  lead_count: number;
  created_at: string;
}

export interface WorkflowCampaign {
  id: string;
  name: string;
  status: string;
  sender_profile_id: string | null;
  sender_profile: { id: string; from_name: string; from_email: string } | null;
}

export interface WorkflowSequence {
  id: string;
  name: string;
  status: string;
  steps?: unknown[];
}

export interface Workflow {
  id: string;
  organisation_id: string;
  user_id: string;
  title: string;
  segment_id: string | null;
  campaign_id: string | null;
  sequence_id: string | null;
  status: 'draft' | 'active' | 'paused' | 'completed';
  activated_at: string | null;
  schedule_type: 'immediately' | 'later';
  schedule_scheduled_at: string | null;
  schedule_from: string | null;
  schedule_to: string | null;
  schedule_daily_limit: number | null;
  schedule_timezone: string | null;
  estimated_leads_count: number;
  email_pending_count: number;
  email_sending_count: number;
  email_sent_count: number;
  email_failed_count: number;
  credits_exhausted: boolean;
  segment: WorkflowSegment | null;
  campaign: WorkflowCampaign | null;
  sequence: WorkflowSequence | null;
  filters: WorkflowFilter[];
  leads: WorkflowLead[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export type SendingStatus = 'pending' | 'sending' | 'failed' | 'done' | null;

export function getWorkflowSendingStatus(wf: Workflow): SendingStatus {
  const total = wf.email_pending_count + wf.email_sending_count + wf.email_sent_count + wf.email_failed_count;
  if (total === 0) return wf.status === 'draft' ? null : 'pending';
  if (wf.email_sending_count > 0 || (wf.email_sent_count > 0 && wf.email_pending_count > 0)) return 'sending';
  if (wf.email_failed_count > 0) return 'failed';
  if (wf.email_sent_count > 0 && wf.email_pending_count === 0 && wf.email_sending_count === 0) return 'done';
  return 'pending';
}

export interface WorkflowCounts {
  all: number;
  active: number;
  draft: number;
  paused: number;
  completed: number;
}

export interface WorkflowListResponse {
  workflows: Workflow[];
  counts: WorkflowCounts;
}

export interface WorkflowListParams {
  status?: string;
  search?: string;
  sort_by?: string;
  page?: number;
  per_page?: number;
}

export interface PreviewLead {
  id: string;
  name: string;
  company: string;
  title: string;
  industry: string;
  location: string | null;
  email: string | null;
  source: string | null;
}

export interface PreviewLeadResponse {
  leads: PreviewLead[];
}

export type EmailLogStatus =
  | 'pending' | 'queued' | 'sending' | 'sent'
  | 'failed' | 'bounced' | 'complained' | 'skipped' | 'unsubscribed';

export interface WorkflowEmailLog {
  id: string;
  to_email: string;
  to_name: string | null;
  from_email: string;
  from_name: string;
  company: string | null;
  subject: string;
  email_body: string;
  status: EmailLogStatus;
  failure_reason: string | null;
  step_number: number;
  variant_label: string;
  scheduled_for: string;
  sent_at: string | null;
  message_id: string | null;
  created_at: string;
  unsubscribed_at?: string | null;
}

export interface EmailLogSummary {
  pending: number;
  sending: number;
  sent: number;
  failed: number;
  total: number;
}

export interface WorkflowEmailLogsResponse {
  logs: WorkflowEmailLog[];
  summary: EmailLogSummary;
}

export interface WorkflowEmailLogsParams {
  status?: string;
  page?: number;
  per_page?: number;
  search_lead?: string;
  search_sender?: string;
  search_company?: string;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useWorkflowsQuery(params: WorkflowListParams) {
  return useQuery({
    queryKey: ['workflows', params],
    queryFn: () => get<WorkflowListResponse>(API_URL.WORKFLOWS.LIST, params as Record<string, unknown>),
    placeholderData: (prev) => prev,
  });
}

export function useWorkflowQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['workflow', id],
    queryFn: () => get<Workflow>(API_URL.WORKFLOWS.GET(id!)),
    enabled: !!id,
  });
}

export function useWorkflowLeadPreviewQuery(
  id: string | undefined,
  params?: {
    page?: number;
    per_page?: number;
    segment_id?: string;
    filters?: Array<{ field: string; operator: string; value: string }>;
  },
) {
  return useQuery({
    queryKey: ['workflow-lead-preview', id, params],
    queryFn: () =>
      get<PreviewLeadResponse>(API_URL.WORKFLOWS.LEADS_PREVIEW(id!), params as Record<string, unknown>),
    // Only run when both workflowId and params are provided — the caller passes
    // params only when filters are active, so this query stays idle otherwise.
    enabled: !!id && params !== undefined,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useCreateWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      post<Workflow>(API_URL.WORKFLOWS.CREATE, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function useUpdateWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      patch<Workflow>(API_URL.WORKFLOWS.UPDATE(id), data),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['workflow-lead-preview', variables.id] });
    },
  });
}

export function useDeleteWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(API_URL.WORKFLOWS.DELETE(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function useActivateWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Workflow>(API_URL.WORKFLOWS.ACTIVATE(id), {}),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow', id] });
    },
  });
}

export function usePauseWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Workflow>(API_URL.WORKFLOWS.PAUSE(id), {}),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workflow', id] });
    },
  });
}

export function useDuplicateWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<Workflow>(API_URL.WORKFLOWS.DUPLICATE(id), {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

export function useSaveFiltersMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, filters }: { id: string; filters: Array<{ field: string; operator: string; value: string }> }) =>
      post<{ filters: WorkflowFilter[] }>(API_URL.WORKFLOWS.FILTERS(id), { filters }),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workflow', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['workflow-lead-preview', variables.id] });
    },
  });
}

export function useSaveLeadsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      leads,
      select_all,
    }: {
      id: string;
      leads: Array<{ lead_provider_id: string; is_checked: boolean }>;
      select_all?: boolean;
    }) => post<{ saved: number }>(API_URL.WORKFLOWS.LEADS(id), { leads, ...(select_all ? { select_all: true } : {}) }),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workflow', variables.id] });
    },
  });
}

export function useRetryFailedEmailsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      post<{ retried: number }>(API_URL.WORKFLOWS.EMAIL_LOGS_RETRY_FAILED(id), {}),
    onSuccess: (_res, id) => {
      queryClient.invalidateQueries({ queryKey: ['workflow-email-logs', id] });
      queryClient.invalidateQueries({ queryKey: ['workflow', id] });
    },
  });
}

export function useWorkflowEmailLogsQuery(
  workflowId: string | undefined,
  params: WorkflowEmailLogsParams,
) {
  return useQuery({
    queryKey: ['workflow-email-logs', workflowId, params],
    queryFn: () =>
      get<WorkflowEmailLogsResponse>(
        API_URL.WORKFLOWS.EMAIL_LOGS(workflowId!),
        params as Record<string, unknown>,
      ),
    enabled: !!workflowId,
    placeholderData: (prev) => prev,
  });
}
